import React from 'react';
import { motion } from 'framer-motion';
import { Cat, ShoppingBag, Globe, TrendingUp } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { BUSINESS_SERVICES } from '@/data/index';
import { BusinessCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';

const Business: React.FC = () => {
  const { t, isEn } = useLanguage();

  const pageContent = {
    title: {
      jp: "事業内容",
      en: "Our Business"
    },
    subtitle: {
      jp: "多文化理解と専門性を軸に、持続可能な価値を創造する4つの主要事業。",
      en: "Four core business areas creating sustainable value through multicultural understanding and expertise."
    },
    introText: {
      jp: "株式会社M.M.Kは、日本市場の深い理解と国際的なネットワークを融合させ、ペット関連事業からクロスボーダー支援まで、多角的なソリューションを提供しています。各事業において、法令遵守と長期的な信頼関係の構築を最優先事項としています。",
      en: "M.M.K Co., Ltd. combines deep understanding of the Japanese market with international networks to provide diverse solutions, from pet-related businesses to cross-border support. In every business line, we prioritize legal compliance and the building of long-term trust."
    }
  };

  // Transform data to bridge type differences between data source and card component
  const services = BUSINESS_SERVICES.map(service => ({
    id: service.id,
    title: { jp: service.title.ja, en: service.title.en },
    description: { jp: service.description.ja, en: service.description.en },
    icon: (
      {
        'cat-hotel': Cat,
        'pet-products': ShoppingBag,
        'cross-border': Globe,
        'marketing': TrendingUp,
      }[service.id] || Globe
    ),
    link: service.link,
    linkText: service.linkText ? { jp: service.linkText.ja, en: service.linkText.en } : undefined
  }));

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.OFFICE_BUILDING_5} 
            alt="Business background" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/60 to-background" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-primary mb-4"
          >
            {t(pageContent.title)}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            {t(pageContent.subtitle)}
          </motion.p>
        </div>
      </section>

      {/* Intro Description */}
      <section className="py-12 bg-card/50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto border-l-4 border-primary pl-6 md:pl-10"
          >
            <p className="text-lg leading-relaxed text-foreground/80">
              {t(pageContent.introText)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Business Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <BusinessCard service={service} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Note Section (SEO & Trust) */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-2xl font-semibold text-primary">
              {isEn ? "Our Commitment to Quality" : "品質への取り組み"}
            </h2>
            <p className="text-muted-foreground">
              {isEn 
                ? "Every business we operate is built on the foundation of Japanese precision and international adaptability. We don't just provide services; we build bridges between cultures, ensuring that brands and businesses can thrive in the complex global marketplace while maintaining the highest standards of animal welfare and operational integrity."
                : "私たちが提供するすべての事業は、日本的な緻密さと国際的な適応力を基盤としています。単にサービスを提供するだけでなく、文化の架け橋となり、動物福祉や誠実な運営という最高水準を維持しながら、ブランドやビジネスが複雑なグローバル市場で繁栄できるよう支援します。"}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Business;